import { Link, Tag, Typography, TYPOGRAPHY_VARIANT } from '@tajawal/ct-web-design-system';
import { useTranslation } from 'react-i18next';
import { EXAMPLE_AIPORT_CODE, PLATFORM } from '@/constants/common.constants';
import Styled from './NearbyAirportBanner.styled';
import { useEffect, useRef } from 'react';
import useIsInViewport from '@/hooks/useIsInViewport';
import { SearchRequestModel } from '@tajawal/web-flights-sdk/lib/types/search-request.types';
import { pushNearbyAirportsCardClicked, pushNearbyAirportsCardViewed } from '@/utils/avoGtm.utils';
import { useService } from '@/contexts/ServiceContext';
import { Platform } from '@/types/common.types';
import { DomesticItinerary, ItineraryModel } from '@tajawal/web-flights-sdk/lib/types/itinerary.types';
import _find from 'lodash/find';
import { LegModel } from '@tajawal/web-flights-sdk/lib/types/leg.types';
import { FlightDirection } from '@tajawal/web-flights-sdk/lib/types/common.types';

type NearbyAirportBannerProps = {
  onHideNearby: (shouldHide: boolean) => void;
  searchRequest: SearchRequestModel;
  platform?: Platform;
  firstNearbyItinerary: ItineraryModel | DomesticItinerary;
  direction?: FlightDirection;
};

function NearbyAirportBanner({
  onHideNearby,
  searchRequest,
  platform = PLATFORM.MOBILE,
  firstNearbyItinerary,
  direction = null,
}: NearbyAirportBannerProps) {
  const { t } = useTranslation();

  const wrapperRef = useRef();
  const isInView = useIsInViewport(wrapperRef);
  const isViewdOnce = useRef(false);

  const { resources } = useService();

  const isDesktop = platform === PLATFORM.DESKTOP;

  const getNearbyId = () => {
    const { originId, destinationId } = searchRequest || {};
    const iatas = [originId, destinationId];
    const nearbyLeg = _find(
      firstNearbyItinerary?.legs,
      (leg: LegModel) => !iatas.includes(leg?.originId) || !iatas.includes(leg?.destinationId)
    );
    return nearbyLeg?.originId || nearbyLeg?.destinationId;
  };

  useEffect(() => {
    if (isInView && !isViewdOnce.current) {
      pushNearbyAirportsCardViewed({
        originId: searchRequest?.originId,
        destinationId: searchRequest?.destinationId,
        $resources: resources,
        nearbyId: getNearbyId(),
        flightDirection: direction,
      });
      isViewdOnce.current = true;
    }
  }, [searchRequest, isInView]);

  const handleNearbyHiding = (shouldHide: boolean) => {
    onHideNearby(shouldHide);
    pushNearbyAirportsCardClicked({
      originId: searchRequest?.originId,
      destinationId: searchRequest?.destinationId,
      $resources: resources,
      shouldHide,
      nearbyId: getNearbyId(),
      flightDirection: direction,
    });
  };

  return (
    <Styled.Wrapper ref={wrapperRef} $isDesktop={isDesktop}>
      <Typography variant={TYPOGRAPHY_VARIANT.HEADING4SEMBLD} component="h4">
        {t('SearchResults.NearbyAirportBanner')}
      </Typography>
      <Styled.Footer $isDesktop={isDesktop}>
        <Styled.ExampleWrapper>
          <Typography component="span" variant={TYPOGRAPHY_VARIANT.HEADING4SEMBLD}>
            {t('Common.Example')}
          </Typography>
          <Tag type="filled" appearance="buttercup">
            {EXAMPLE_AIPORT_CODE as string}
          </Tag>
        </Styled.ExampleWrapper>
        <Styled.LinkGroup>
          {/* eslint-disable-next-line  */}
          <Link noPadding onClick={() => handleNearbyHiding(false)}>
            {t('Common.Yes') as string}
          </Link>
          {/* eslint-disable-next-line  */}
          <Link noPadding onClick={() => handleNearbyHiding(true)}>
            {t('Common.No') as string}
          </Link>
        </Styled.LinkGroup>
      </Styled.Footer>
    </Styled.Wrapper>
  );
}

export default NearbyAirportBanner;
